import { SimplePlateEditor } from '@/components/simple-plate-editor';
import { useToast } from '@/components/ui/use-toast';
import { useGetOrCreateEntityNote, useUpdateNote } from '@/services/query/notes-queries';
import { useCallback, useEffect, useState } from 'react';
import { Descendant } from 'slate';

interface EntityNotesEditorProps {
  entityId: string | number;
  readOnly?: boolean;
}

// Use the same element type as in SimplePlateEditor
const ELEMENT_PARAGRAPH = 'paragraph';

// Default initial value for the editor
const defaultInitialValue: Descendant[] = [
  {
    type: ELEMENT_PARAGRAPH,
    children: [
      { text: '' }
    ],
  },
];

export const EntityNotesEditor = ({ entityId, readOnly = false }: EntityNotesEditorProps) => {
  const [value, setValue] = useState<Descendant[]>(defaultInitialValue);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'unsaved' | 'saving'>('saved');
  const { toast } = useToast();

  // localStorage key for this entity's notes
  const localStorageKey = `entity-notes-${entityId}`;

  // Track if we've initialized the editor to prevent unnecessary updates
  const [isEditorInitialized, setIsEditorInitialized] = useState(false);

  // Use TanStack Query hooks
  const noteQuery = useGetOrCreateEntityNote(entityId);
  const updateNoteMutation = useUpdateNote();

  // Load from localStorage on mount
  useEffect(() => {
    const savedContent = localStorage.getItem(localStorageKey);
    if (savedContent) {
      try {
        const parsedContent = JSON.parse(savedContent);
        setValue(parsedContent);
        setSaveStatus('unsaved');
      } catch (e) {
        console.error('Failed to parse localStorage content:', e);
        localStorage.removeItem(localStorageKey);
      }
    }
  }, [localStorageKey]);

  // Set editor value when note data is loaded (only once, unless localStorage has content)
  useEffect(() => {
    if (noteQuery.data && !isEditorInitialized) {
      // Check if we have localStorage content first
      const savedContent = localStorage.getItem(localStorageKey);
      if (savedContent) {
        setIsEditorInitialized(true);
        return;
      }

      let initialValue;

      if (noteQuery.data.content && Array.isArray(noteQuery.data.content) && noteQuery.data.content.length > 0) {
        initialValue = noteQuery.data.content;
      } else if (typeof noteQuery.data.content === 'string') {
        try {
          const parsedContent = JSON.parse(noteQuery.data.content);
          initialValue = Array.isArray(parsedContent) ? parsedContent : defaultInitialValue;
        } catch (e) {
          console.error('Failed to parse content string:', e);
          initialValue = defaultInitialValue;
        }
      } else {
        initialValue = defaultInitialValue;
      }

      setValue(initialValue);
      setSaveStatus('saved');
      setIsEditorInitialized(true);
    }
  }, [noteQuery.data, isEditorInitialized, localStorageKey]);

  // Auto-save to localStorage when content changes
  const handleContentChange = useCallback((newValue: Descendant[]) => {
    setValue(newValue);
    setSaveStatus('unsaved');

    // Save to localStorage
    try {
      localStorage.setItem(localStorageKey, JSON.stringify(newValue));
      console.log('Content saved to localStorage');
    } catch (e) {
      console.error('Failed to save to localStorage:', e);
    }
  }, [localStorageKey]);

  // Save notes function
  const handleSave = useCallback(() => {
    if (!noteQuery.data || !entityId || readOnly) return;

    setSaveStatus('saving');

    // Ensure we have valid content to save
    let contentToSave = value;

    // If the content is empty or invalid, create a default empty paragraph
    if (!contentToSave || !Array.isArray(contentToSave) || contentToSave.length === 0) {
      contentToSave = [
        {
          type: ELEMENT_PARAGRAPH,
          children: [{ text: '' }]
        }
      ];
    }

    console.log('Saving content:', contentToSave);

    updateNoteMutation.mutate({
      noteId: noteQuery.data.id,
      entityId,
      data: { content: contentToSave }
    }, {
      onSuccess: () => {
        setSaveStatus('saved');
        // Clear localStorage after successful save
        localStorage.removeItem(localStorageKey);
        toast({
          title: 'Success',
          description: 'Notes saved',
        });
      },
      onError: (error) => {
        setSaveStatus('unsaved');
        console.error('Error saving notes:', error);
        toast({
          title: 'Error',
          description: 'Failed to save notes',
          variant: 'destructive',
        });
      }
    });
  }, [noteQuery.data, entityId, readOnly, value, updateNoteMutation, localStorageKey, toast]);

  return (
    <div className="w-full">
      {noteQuery.isLoading ? (
        <div className="flex items-center gap-2 py-6 px-1">
          <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-pulse" />
          <p className="text-sm text-muted-foreground">Loading notes…</p>
        </div>
      ) : (
        <SimplePlateEditor
          value={value}
          onChange={handleContentChange}
          readOnly={readOnly}
          onSave={handleSave}
          isSaving={updateNoteMutation.isPending}
          saveStatus={saveStatus}
        />
      )}
    </div>
  );
};

export default EntityNotesEditor;